import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { interactionsService } from '@/api';
import { useToast } from '@/components/ui';
import { useAuth } from '@/providers';
import type { FeedItem } from '@/types';

export function useLibrary() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch saved items
  const savedQuery = useQuery({
    queryKey: ['saved', user?.id],
    queryFn: () => interactionsService.getSaved(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch liked items
  const likedQuery = useQuery({
    queryKey: ['liked', user?.id],
    queryFn: () => interactionsService.getLiked(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Unsave mutation
  const unsaveMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.unsave(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['saved', user?.id] });
      const previous = queryClient.getQueryData(['saved', user?.id]);

      queryClient.setQueryData(['saved', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });

      // Also update feed cache
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.content.id === contentId
              ? { ...item, userState: { ...item.userState, isSaved: false } }
              : item,
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _contentId, context) => {
      queryClient.setQueryData(['saved', user?.id], context?.previous);
      toast('Erro ao remover item', 'error');
    },
    onSuccess: () => {
      toast('Item removido dos salvos', 'success');
    },
  });

  // Unlike mutation
  const unlikeMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.unlike(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['liked', user?.id] });
      const previous = queryClient.getQueryData(['liked', user?.id]);

      queryClient.setQueryData(['liked', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });

      // Also update feed cache
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.content.id === contentId
              ? { ...item, userState: { ...item.userState, isLiked: false } }
              : item,
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _contentId, context) => {
      queryClient.setQueryData(['liked', user?.id], context?.previous);
      toast('Erro ao remover item', 'error');
    },
    onSuccess: () => {
      toast('Item removido dos curtidos', 'success');
    },
  });

  return {
    savedItems: savedQuery.data?.items ?? [],
    likedItems: likedQuery.data?.items ?? [],
    isLoading: savedQuery.isLoading || likedQuery.isLoading,
    isError: savedQuery.isError || likedQuery.isError,
    unsave: unsaveMutation.mutate,
    unlike: unlikeMutation.mutate,
  };
}
